// src/components/PatientDetails/PatientDetails.tsx
// -----------------------------------------------------------------------------
// PatientDetails - Main patient details page
// -----------------------------------------------------------------------------
// Uses the three-column responsive layout:
// - Left: Patient data and tables (resizable, collapsible)
// - Center: Chat interface with domain selector
// - Right: Action buttons
// -----------------------------------------------------------------------------

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  athenaDarkPaperSx,
  athenaBackdropSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaDarkActionsSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
  athenaHeaderIconBoxSx,
  athenaColors,
} from "../layout/AthenaDialogStyles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import logger from "utils/logger";
import { createData } from "api/apiHelpers";

// Hooks
import useDocuments from "hooks/useDocuments";
import useDomainTypes from "hooks/useDomainTypes";
import { useNotification } from "hooks/useNotification";
import useTables from "hooks/useTables";
import usePatient from "hooks/usePatient";
import useTutorial from "hooks/useOnboardingTutorial";

// Layout
import PatientDetailsLayout from "./PatientDetailsLayout";
import DocumentModal from "components/layout/modals/DocumentModal";
import SharePatientModal from "./SharePatientModal";
import ExportDialog from "components/dashboards/ExportDialog";

// Types
import { EvidenceDocument } from "types";

// Helpers
import { PendingItem } from "./sections/UnifiedCaptureHub/types";
import { useTranslation } from "../../i18n";

const PatientDetails = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const notification = useNotification();
  const navigate = useNavigate();

  // Patient info
  const {
    patient,
    patientId,
    isLoading: patientLoading,
    error: patientError,
    deletePatient,
  } = usePatient();

  // Domain types and selection
  const {
    domainTypes,
    selectedDomains,
    suggestedDomains,
    isProcessing,
    isStaleProcessing,
    hasExtractionErrors,
    legacyTablesEnabled,
    handleDomainSelectionChange,
    refreshSuggestions,
    refreshActiveDomains,
    pollActiveDomains,
    retryExtraction,
  } = useDomainTypes();

  // Tables data
  const { tablesData, generateTables, pollTablesData, refreshTablesData } =
    useTables(selectedDomains);

  // Documents
  const { documents, fetchDocuments, handleUpload, handleDelete } =
    useDocuments();

  // Tutorial (skip real extraction pipeline during onboarding)
  const { isActive: tutorialActive } = useTutorial();

  // State
  const [tablesLoading, setTablesLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);

  // Track if we've already started auto-polling to avoid duplicate polls
  const autoPollingRef = useRef(false);
  // Track previous isProcessing value to detect transitions
  const prevProcessingRef = useRef(false);

  // Poll tables on page load and domain selection change
  useEffect(() => {
    pollTablesData();
  }, [selectedDomains, pollTablesData]);

  // Refresh tables when tutorial seed completes, then signal the overlay.
  // Uses refreshActiveDomains (single fetch) instead of pollActiveDomains
  // to avoid setting isProcessing=true, which would trigger the auto-poll
  // loop and leave the "Extracting data" banner visible.
  // Also clears tablesLoading and claims autoPollingRef so that the
  // concurrent handleSubmit delay-loop doesn't restart polling.
  useEffect(() => {
    const handler = async () => {
      await refreshActiveDomains();
      await pollTablesData();
      await refreshSuggestions();
      setTablesLoading(false);
      autoPollingRef.current = true;
      window.dispatchEvent(new Event("tutorial-tables-ready"));
    };
    window.addEventListener("tutorial-seed-complete", handler);
    return () => window.removeEventListener("tutorial-seed-complete", handler);
  }, [refreshActiveDomains, pollTablesData, refreshSuggestions]);

  // Auto-poll whenever isProcessing transitions to true (not just on mount).
  // This covers:
  //   - Page loaded while extraction is in progress
  //   - User submits documents and extraction starts while on the page
  //   - Retry extraction triggered from the error banner
  useEffect(() => {
    const wasProcessing = prevProcessingRef.current;
    prevProcessingRef.current = isProcessing;

    if (isProcessing && !wasProcessing && !autoPollingRef.current) {
      autoPollingRef.current = true;
      logger.debug("[PatientDetails] Processing detected, starting poll loop");

      const pollUntilDone = async () => {
        const completed = await pollActiveDomains();
        logger.debug("[PatientDetails] Poll completed, success:", completed);
        // Allow React to flush selectedDomains state from refreshActiveDomains
        // before pollTablesData reads domainsRef.current
        await new Promise((r) => setTimeout(r, 500));
        await pollTablesData();
        await refreshSuggestions();
        autoPollingRef.current = false;
      };

      pollUntilDone();
    }
  }, [isProcessing, pollActiveDomains, pollTablesData, refreshSuggestions]);

  // Convert blob → File for uploads
  const blobToFile = (b: Blob, filename: string, type?: string) =>
    new File([b], filename, {
      type: type || b.type || "application/octet-stream",
    });

  /** Handle submission from chat interface */
  const handleSubmit = async () => {
    // During the tutorial the seed endpoint populates data directly.
    // Skip everything: no tablesLoading flag, no fetchDocuments, no
    // generateTables — all of which would trigger the "Extracting data"
    // banner or polling loops.
    if (tutorialActive) return;

    setTablesLoading(true);

    try {
      await fetchDocuments();

      if (selectedDomains.length > 0) {
        await generateTables();
      }

      // Wait for backend to start processing, then poll with retries.
      // A single 2s delay often isn't enough — the document may not be in
      // a processing state yet when refreshActiveDomains fires, so we retry
      // with exponential backoff to ensure polling starts reliably.
      const delays = [2000, 4000, 8000];
      let started = false;
      for (const delay of delays) {
        await new Promise((r) => setTimeout(r, delay));
        await refreshActiveDomains();
        if (isProcessing || autoPollingRef.current) {
          started = true;
          break;
        }
      }

      // If the useEffect-based poll didn't start (timing edge case),
      // kick off pollActiveDomains directly as a fallback.
      if (!started && !autoPollingRef.current) {
        logger.debug("[PatientDetails] Forcing direct poll after upload");
        autoPollingRef.current = true;
        const completed = await pollActiveDomains();
        logger.debug("[PatientDetails] Direct poll completed, success:", completed);
        await new Promise((r) => setTimeout(r, 500));
        await pollTablesData();
        await refreshSuggestions();
        autoPollingRef.current = false;
      } else {
        await pollTablesData();
      }

      logger.debug("[Tables] Initial poll complete, auto-poll will continue if processing");
    } finally {
      setTablesLoading(false);
    }
  };

  /** Handle submission finished (from chat interface) */
  const handleSubmissionFinished = async () => {
    await fetchDocuments();
    await pollActiveDomains();
    await pollTablesData();
    await refreshSuggestions();
    logger.debug("✅ Submission and polling finished");
  };

  // Filter documents for dropdown (visualizable files only)
  const visualizableDocuments = (documents as EvidenceDocument[]).filter(
    (doc) => doc.file_url || doc.file
  );

  // Handle share patient
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const handleShare = useCallback(() => {
    setShareModalOpen(true);
  }, []);

  // Handle generate report — calls LLM endpoint with checksum caching
  const [reportContent, setReportContent] = useState<string | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const handleGenerateReport = useCallback(async () => {
    if (!patientId) return;
    setReportLoading(true);
    setReportModalOpen(true);
    try {
      const res = await createData<Record<string, never>, { report: string; cached: boolean }>(
        `/patients/${patientId}/generate-report/`,
        {}
      );
      setReportContent(res.report);
      if (res.cached) {
        notification.info(t("patient_details.report_cached", "Report loaded from cache (no data changes detected)"));
      } else {
        notification.success(t("patient_details.report_generated", "Report generated successfully"));
      }
    } catch (err: any) {
      logger.error("[Report] Failed:", err);
      notification.error(t("patient_details.report_failed", "Report generation failed"));
      setReportModalOpen(false);
    } finally {
      setReportLoading(false);
    }
  }, [patientId, notification]);

  // Export dialog — reuses the dashboard ExportDialog scoped to this patient
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const handleExportData = useCallback(() => {
    setExportDialogOpen(true);
    return Promise.resolve();
  }, []);

  // Loading state
  if (patientLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 64px)",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">{t("patient_details.loading", "Loading patient data...")}</Typography>
      </Box>
    );
  }

  // Patient not found — redirect to 404 page
  if (!patientLoading && !patient && patientError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 64px)",
          gap: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("patient_details.not_found_title", "Patient Not Found")}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
          {t(
            "patient_details.not_found_message",
            "The patient you're looking for doesn't exist or has been deleted."
          )}
        </Typography>
        <Button variant="contained" onClick={() => navigate("/dashboard", { replace: true })}>
          {t("patient_details.back_to_dashboard", "Back to Dashboard")}
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* Main Layout */}
      <PatientDetailsLayout
        patientId={patientId}
        patientName={patient?.name}
        anonymizedPatientName={patient?.anonymized_name}
        environmentNames={patient?.environment_names}
        patientUpdatedAt={patient?.modified_at}
        patientDomains={patient?.domains}
        legacyTablesEnabled={legacyTablesEnabled}
        canRevealPii={patient?.can_reveal_pii}
        externalId={patient?.external_id}
        legacyId={patient?.legacy_id}
        domainTypes={domainTypes}
        selectedDomains={selectedDomains}
        onDomainSelectionChange={handleDomainSelectionChange}
        suggestedDomains={suggestedDomains}
        tablesData={tablesData}
        tablesLoading={tablesLoading}
        isAIExtracting={isProcessing}
        submittedDocuments={visualizableDocuments}
        allDocuments={documents}
        onDocumentDelete={async (docId) => {
          await handleDelete(docId);
          await fetchDocuments();
        }}
        onSubmit={handleSubmit}
        isExtracting={isProcessing || tablesLoading}
        isStaleProcessing={isStaleProcessing}
        hasExtractionErrors={hasExtractionErrors}
        onRetryExtraction={retryExtraction}
        onDeletePatient={deletePatient}
        onSharePatient={handleShare}
        onGenerateReport={handleGenerateReport}
        onExportData={handleExportData}
        onDomainAdd={async (domainKey: string) => {
          // Auto-activate the domain by adding it to the current selection
          const newDomains = [...selectedDomains, domainKey];
          await handleDomainSelectionChange(newDomains);
        }}
        onRecordCreated={async () => {
          // Refresh table data only — no AI extraction polling for manual record
          // creation. refreshTablesData refetches immediately and, when a
          // universal event-style row is still awaiting async terminology
          // resolution (medications: resolution_status "pending"), follows up
          // with a couple of bounded refetches so resolved codes fill in live.
          await refreshTablesData();
        }}
      />

      {/* Document Modal */}
      <DocumentModal
        docs={selectedDoc ? [selectedDoc] : []}
        index={selectedDoc ? 0 : null}
        onClose={() => setSelectedDoc(null)}
        onNavigate={() => {}}
      />

      {/* Share Patient Modal */}
      {patientId && (
        <SharePatientModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          patientId={patientId}
          patientName={patient?.anonymized_name || patient?.name}
        />
      )}

      {/* Export Dialog — reuses dashboard export modal scoped to this patient */}
      {patientId && (
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          patientIds={[Number(patientId)]}
        />
      )}

      {/* Clinical Report Modal */}
      <Dialog
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: athenaDarkPaperSx }}
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          <Box sx={athenaHeaderIconBoxSx}>
            <DescriptionOutlinedIcon sx={{ color: athenaColors.periwinkle }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {t("patient_details.report_title", "Clinical History Report")}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setReportModalOpen(false)}
            size="small"
            sx={{ color: alpha(theme.palette.common.white, 0.5), "&:hover": { color: theme.palette.common.white } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          {reportLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2.5 }}>
              <CircularProgress sx={{ color: athenaColors.periwinkle }} />
              <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.6), fontStyle: "italic" }}>
                {t("patient_details.generating_report", "Generating clinical history report...")}
              </Typography>
            </Box>
          ) : reportContent ? (
            <Box
              sx={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                lineHeight: 1.8,
                py: 1,
                "& h1, & h2, & h3": { mt: 2, mb: 1, color: theme.palette.common.white },
              }}
            >
              <Typography variant="body1" component="div" sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
                {reportContent}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ color: alpha(theme.palette.common.white, 0.5), py: 4, textAlign: "center" }}>
              {t("patient_details.no_report", "No report available.")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={athenaDarkActionsSx}>
          {reportContent && (
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                navigator.clipboard.writeText(reportContent);
                notification.success(t("patient_details.report_copied", "Report copied to clipboard"));
              }}
              sx={athenaDarkPrimaryButtonSx}
            >
              {t("patient_details.copy_clipboard", "Copy to Clipboard")}
            </Button>
          )}
          <Button
            onClick={() => setReportModalOpen(false)}
            sx={athenaDarkSecondaryButtonSx}
          >
            {t("action.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default PatientDetails;
